/*
 * @description:
 * @author: Jason Wong
 * @since: 2022-04-14 15:11:29
 */
/*
 * utility for teachers and students to connect to websocket service
 * @author: Jason Wong
 * @since: 2019-03-21 10:57:29
 */

import { uuid } from "../uuid";
import Order from "./order";
import Message from "./message";
import MessageDispatcher from "./messageDispatcher";
import { isBlank } from "../String";

/* heart check's interval for client to check whether online*/
const heartCheckInterval = 10 * 1000;

class SendOption {
  constructor(needResponse) {
    this._needResponse = needResponse != null ? needResponse : true;
  }

  get needResponse() {
    return this._needResponse;
  }
}

class AnswerMessage {
  code;
  msg;

  constructor(msg, code) {
    this.msg = msg;
    this.code = code;
  }

  toString() {
    return this.msg;
  }
}

const DEFAULT_SEND_OPTIONS = new SendOption(true);
export const ONE_WAY_SEND_OPTIONS = new SendOption(false);

const TIMEOUT_ERROR_TEXT = "请求超时，建议稍候再试。";
const TIMEOUT_ERROR_MESSAGE = new Message(false, TIMEOUT_ERROR_TEXT);

const SERVER_HEART_BEAT = 'ping';

/**
 * main class of webscoket client
 */
class WSClientBase {
  constructor(websocketAddress,protocals, params) {
    this._websocketAddress = websocketAddress;
    this._watchInstanceMap = new Map();
    this._dispatchers = [];
    this._lastSendTime = 0;
    this._protocals = protocals;
    this._timeout = params && params.timeout ? params.timeout : 300 * 1000;
    this._consoleMessage = params && params.consoleMessage ? true : false;
    this._autoPing = !!params && params.autoPing != null ? params.autoPing : true;
    this._extraHeartBeatAction = !!params && !!params.heartBeat ? params.heartBeat : null;
  }

  _initWs() {
    var self = this;
    this._messageHandler = (ev) => {
      if (isBlank(ev.data) || ev.data === SERVER_HEART_BEAT) return;
      var msg = JSON.parse(ev.data);
      if (msg.name && msg.name == "answer") {
        console.log("#-# handler?");
        const watchInstance = !!msg.data && !!msg.data.sourceId ? this._watchInstanceMap.get(msg.data.sourceId) : null;
        if (!!watchInstance) {
          if (msg.data.isSuccessful) {
            watchInstance.resolve(msg.data);
          } else {
            watchInstance.reject(new AnswerMessage(msg.data.msg, msg.data.code));
          }
          this._watchInstanceMap.delete(msg.sourceId);
        }
      } else {
        for (var dis of self._dispatchers) {
          if (dis.canDeal(msg)) {
            dis.deal(msg.data);
            break;
          }
        }
      }
    };
    this._ws.addEventListener("message", this._messageHandler);

    this._errorHandler = (ev) => {
      this._closeHeartCheck();
      this.raise(new Order("errored"));
    };
    this._ws.addEventListener("error", this._errorHandler);

    this._closeHandler = (ev) => {
      this._closeHeartCheck();
      for (const w of this._watchInstanceMap.values()) {
        w.reject(Message.createErrorMessage("connection is closed."));
      }
      this._watchInstanceMap.clear();
      this.raise(new Order("closed"));

      this._ws = null;
    };
    this._ws.addEventListener("close", this._closeHandler);
  }

  _startHeartCheck() {
    this._closeHeartCheck();
    this._heartChecker = setInterval(() => {
      //if (new Date() - this._lastSendTime < this._timeout) return;
      this._ws && this._autoPing && this.send("ping", {}, new SendOption(false));
      this._extraHeartBeatAction && this._extraHeartBeatAction();
    }, heartCheckInterval);
  }
  _closeHeartCheck() {
    if (this._heartChecker != -1) {
      clearInterval(this._heartChecker);
      this._heartChecker = -1;
    }
  }

  _send(msg, sendOption = DEFAULT_SEND_OPTIONS) {
    if (!this._ws) {
      return Promise.reject(Message.createErrorMessage("please connect firstly."));
    }

    msg.id = uuid();

    return new Promise((res, rej) => {
      try {
        this._ws.send(JSON.stringify(msg));
        this._lastSendTime = new Date();
        if (this._consoleMessage) {
          console.log(`message sended:`, msg);
        }

        const newWatchInstance = {
          sourceId: msg.id,
          resolve: res,
          reject: rej,
        };
        this._watchInstanceMap.set(newWatchInstance.sourceId, newWatchInstance);
        if (sendOption.needResponse && msg.name != "ping") {
          setTimeout(() => {
            const watcher = this._watchInstanceMap.get(newWatchInstance.sourceId);
            if (!watcher) return;

            console.log("timeout resource:", msg, this);
            rej(TIMEOUT_ERROR_MESSAGE);
          }, this._timeout);
        }
      } catch (err) {
        rej(Message.createErrorMessage(err));
      }
    });
  }

  raise(msg) {
    for (var dis of this._dispatchers) {
      if (dis.canDeal(msg)) dis.deal(msg);
    }
  }

  /**
   * 绑定事件
   * @param {Fundtion} event
   * @param {*} handler
   */
  on(event, handler) {
    var diss = this._dispatchers.filter((d) => d._orderName == event);
    if (diss.length == 0) {
      var newDis = new MessageDispatcher(event);
      newDis.addHandler(handler);
      this._dispatchers.push(newDis);
    } else {
      diss[0].addHandler(handler);
    }
  }

  /**
   * 取消事件绑定
   * @param {*} event
   * @param {*} handler
   */
  off(event, handler) {
    var diss = this._dispatchers.filter((d) => d._orderName == event);
    if (diss.length > 0) {
      if (handler) diss[0].removeHandler(handler);
      else diss[0].clearHandlers();
    }
  }

  /**
   * 发送命令
   * @param {}} name
   * @param {*} data
   * @param {*} sendOption
   */
  send(name, data, sendOption) {
    return this._send(new Order(name, { data: data }), sendOption);
  }

  /**
   * 连接服务器
   */
  connect() {
    if (this._ws) {
      if (this._ws.readyState == WebSocket.OPEN) {
        return Promise.resolve();
      } else if (this._ws.readyState == WebSocket.CONNECTING) {
        return this._connectPromise;
      }
    }

    this._connectPromise = new Promise((res, rej) => {
      const ws = new WebSocket(this._websocketAddress,this._protocals);
      let doneSymbol = false,
        cancelSymbol = false;
      let timeoutId, connectOpenHandler, connectErrorHandler;
      const connectClear = () => {
        clearTimeout(timeoutId);
        ws.removeEventListener("open", connectOpenHandler);
        ws.removeEventListener("error", connectErrorHandler);
      };

      timeoutId = setTimeout(() => {
        if (doneSymbol) return;

        cancelSymbol = true;
        connectClear();
        res(TIMEOUT_ERROR_MESSAGE);
      }, this._timeout);
      connectOpenHandler = () => {
        console.log("#-# connectOpenHandler", cancelSymbol);
        if (cancelSymbol) {
          ws.close();
        } else {
          doneSymbol = true;
          res(ws);
          connectClear();
        }
        console.log("#-# connect opened!");
      };
      ws.addEventListener("open", connectOpenHandler);
      connectErrorHandler = (err) => {
        if (!cancelSymbol) {
          doneSymbol = true;
          rej(err);
        }
        connectClear();
        console.log("#-# connect error!");
      };
      ws.addEventListener("error", connectErrorHandler);
    }).then((res) => {
      console.log("#-# res", res,this._ws);
      this._ws = res;
      this._initWs();
      this._lastSendTime = new Date();
      this._startHeartCheck();
      this.raise(new Order("opened"));
    });

    return this._connectPromise;
  }

  /**
   * 关闭服务器
   */
  close() {
    this._closeHeartCheck();
    if (this._ws != null) {
      this._ws.removeEventListener("message", this._messageHandler);
      this._ws.removeEventListener("error", this._errorHandler);
      this._ws.removeEventListener("close", this._closeHandler);

      this._dispatchers = [];
      this._watchInstanceMap.clear();
      this._ws.close();
      this._ws = null;
    }
  }

  /**
   * 获取当前状态
   */
  get state() {
    return !this._ws ? WebSocket.CLOSED : this._ws.readyState;
  }
}

export default WSClientBase;
